import { planType } from "../../../constants/RouteTrapTypes";
import { toastMessage } from "../../../constants/strings";
import { PlanTableFormat } from "../../../types/planning/plan.type";
import { finalStatus, planningStatus } from "../../../types/status.type";
import type { EditedPlan } from "./EditRouteConfirmDialog";

export function canRemoveDoor(
  status: string,
  currentDoorId: number | null
): boolean {
  if (!currentDoorId) return true;

  const blockedStatuses: string[] = [
    planningStatus.CLDV,
    planningStatus.TRAP,
    planningStatus.DISP,
    finalStatus.DEPT,
  ];
  return !blockedStatuses.includes(status);
}

export function canRemoveTrailerOrStraightTruck(
  status: string,
  type: PlanTableFormat["type"],
  currentEquipmentId: number | null
): boolean {
  if (!currentEquipmentId) return true;

  if (type === planType.DELIVERY_TRAP) {
    const blockedStatuses: string[] = [planningStatus.TRAP];
    return !blockedStatuses.includes(status);
  } else {
    return true;
  }
}

export function getRemovalBlockedMessage(
  equipment: "door" | "trailer" | "straightTruck",
  status: string,
  type: PlanTableFormat["type"]
): string {
  if (equipment === "door") {
    if (status === planningStatus.TRAP) {
      return toastMessage.inbound.removeDoorNotAllowedAfterTrap;
    } else if (
      status === planningStatus.DISP ||
      status === planningStatus.CLDV ||
      status === finalStatus.DEPT
    ) {
      return toastMessage.inbound.removeDoorNotAllowedAfterCldv;
    } else if (status === planningStatus.CLDK) {
      return type === planType.DELIVERY_TRAP
        ? "Unable to remove the door when the trap is in CLDK status."
        : toastMessage.inbound.removeDoorNotAllowedFromRoute;
    }
  } else {
    const equipmentName =
      equipment === "trailer" ? "Trailer" : "Straight Truck";

    if (status === planningStatus.TRAP) {
      return toastMessage.inbound.removeTrailerOrStraightTruckNotAllowedFromTrapAfterTrap(
        equipmentName
      );
    } else if (status === planningStatus.CLDK) {
      return `Unable to remove the ${equipmentName} when the trap is in CLDK status.`;
    }
  }

  return "";
}

export function shouldShowDoorRemovalError(
  plan: PlanTableFormat,
  editedPlan: EditedPlan,
  hasAttemptedSubmit: boolean,
  isRemoveEquipmentOrDoorValidationEnabled: boolean,
  isFixInUseDoorBlockCldkStatusEnabled: boolean
): boolean {
  if (!isRemoveEquipmentOrDoorValidationEnabled || !plan.door) {
    return false;
  }

  const userTriedToRemoveDoor = editedPlan.doorId === null;

  if (plan.status === planningStatus.CLDK) {
    // Show error when user tries to save with door removed (null)
    return hasAttemptedSubmit && userTriedToRemoveDoor;
  }

  if (!hasAttemptedSubmit) {
    return false;
  }

  const doorRemovalBlocked = !canRemoveDoor(plan.status, plan.door);
  if (isFixInUseDoorBlockCldkStatusEnabled) {
    return doorRemovalBlocked && userTriedToRemoveDoor;
  } else {
    const userTriedToChangeDoor = editedPlan.doorId !== plan.door;
    return doorRemovalBlocked && userTriedToChangeDoor;
  }
}

export function shouldShowTrailerRemovalError(
  plan: PlanTableFormat,
  editedPlan: EditedPlan,
  hasAttemptedSubmit: boolean,
  isRemoveEquipmentOrDoorValidationEnabled: boolean
): boolean {
  if (!isRemoveEquipmentOrDoorValidationEnabled || !plan.trailer) {
    return false;
  }

  const userTriedToRemoveTrailer = editedPlan.trailerEquipmentId === null;
  const userHasStraightTruckSelected =
    editedPlan.straightTruckEquipmentId !== null;

  if (
    plan.status === planningStatus.CLDK &&
    plan.type === planType.DELIVERY_TRAP
  ) {
    // Only show error if user removed trailer AND didn't select straight truck (complete removal)
    // Allow switching from trailer to straight truck
    return (
      hasAttemptedSubmit &&
      userTriedToRemoveTrailer &&
      !userHasStraightTruckSelected
    );
  }

  if (!hasAttemptedSubmit) {
    return false;
  }

  const trailerRemovalBlocked = !canRemoveTrailerOrStraightTruck(
    plan.status,
    plan.type,
    plan.trailer.id
  );
  const userTriedToChangeTrailer =
    editedPlan.trailerEquipmentId !== plan.trailer.id;
  return trailerRemovalBlocked && userTriedToChangeTrailer;
}

export function shouldShowStraightTruckRemovalError(
  plan: PlanTableFormat,
  editedPlan: EditedPlan,
  hasAttemptedSubmit: boolean,
  isRemoveEquipmentOrDoorValidationEnabled: boolean
): boolean {
  if (!isRemoveEquipmentOrDoorValidationEnabled || !plan.straightTruck) {
    return false;
  }

  const userTriedToRemoveStraightTruck =
    editedPlan.straightTruckEquipmentId === null;
  const userHasTrailerSelected = editedPlan.trailerEquipmentId !== null;

  if (
    plan.status === planningStatus.CLDK &&
    plan.type === planType.DELIVERY_TRAP
  ) {
    // Only show error if user removed straight truck AND didn't select trailer (complete removal)
    // Allow switching from straight truck to trailer
    return (
      hasAttemptedSubmit &&
      userTriedToRemoveStraightTruck &&
      !userHasTrailerSelected
    );
  }

  if (!hasAttemptedSubmit) {
    return false;
  }

  const straightTruckRemovalBlocked = !canRemoveTrailerOrStraightTruck(
    plan.status,
    plan.type,
    plan.straightTruck.id
  );
  const userTriedToChangeStraightTruck =
    editedPlan.straightTruckEquipmentId !== plan.straightTruck.id;
  return straightTruckRemovalBlocked && userTriedToChangeStraightTruck;
}
